import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Trash2, Ban, Pencil, FileText, FilePlus, Send } from 'lucide-react';
import { BillingLinesEditor } from './BillingLinesEditor';
import { BillingNotesEditor } from './BillingNotesEditor';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  useBillingDocument,
  useBillingLineItems,
  useBillingPaymentsSync,
  useConvertPolarDocument,
  useDeleteBillingDocument,
  useFigerPolarDocument,
  useMarkBillingPaid,
  useOpenPolarPdf,
  useSendPolarDocument,
  useVoidBillingDocument,
} from '../../hooks/queries/useBillingDocuments';
import { useDossier } from '../../hooks/queries/useDossiers';
import {
  BILLING_DOC_TYPE_LABELS,
  formatEuros,
  type BillingDocumentStatus,
} from '../../services/billing/billingService';
import { polarDocumentNumber, polarPdfId, polarSendId, polarStatusLabel } from '../../services/billing/polarDisplay';
import { BillingSheet } from './BillingSheet';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import { cn } from '../../lib/cn';

import { formatAppDate } from '../../lib/dateFormat';
import { computeLineTotals, formatVatRate, LINE_NATURE_LABELS, type LineNature } from '../../lib/billing/lineTotals';
const STATUS_CLASSES: Record<BillingDocumentStatus, string> = {
  draft:    'bg-neutral-100 text-[#737373]',
  sent:     'bg-[#e9effa] text-[#2b5aa8]',
  paid:     'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  overdue:  'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
  void:     'bg-neutral-100 text-neutral-400',
  accepted: 'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  rejected: 'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
};

type ConfirmKind = 'figer' | 'convert' | 'send' | 'void' | 'delete';

const CONFIRM_COPY: Record<
  ConfirmKind,
  { title: string; body: string; action: string; variant: 'primary' | 'danger' }
> = {
  figer: {
    title: 'Figer dans Polar',
    body: 'Un numéro Polar et un PDF seront générés. CIME n’invente pas de numéro local.',
    action: 'Figer',
    variant: 'primary',
  },
  convert: {
    title: 'Convertir en facture',
    body: 'Polar numérote la facture. La date de prestation est obligatoire.',
    action: 'Convertir',
    variant: 'primary',
  },
  send: {
    title: 'Envoyer via Polar',
    body: 'Devis : e-mail Polar au client. Facture : SuperPDP, même numéro. Pas de mail CIME.',
    action: 'Envoyer',
    variant: 'primary',
  },
  void: {
    title: 'Annuler le document',
    body: 'Les prestations liées seront libérées.',
    action: 'Annuler le document',
    variant: 'danger',
  },
  delete: {
    title: 'Supprimer le brouillon',
    body: 'Cette action est définitive.',
    action: 'Supprimer',
    variant: 'danger',
  },
};

export function BillingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);

  const { data: doc, isLoading, error } = useBillingDocument(id);
  const { data: lines } = useBillingLineItems(id);
  useBillingPaymentsSync(doc ? [doc] : undefined);
  const { data: dossier } = useDossier(doc?.dossier_id);

  const figerMut = useFigerPolarDocument();
  const convertMut = useConvertPolarDocument();
  const pdfMut = useOpenPolarPdf();
  const sendMut = useSendPolarDocument();
  const markPaidMut = useMarkBillingPaid();
  const voidMut = useVoidBillingDocument();
  const deleteMut = useDeleteBillingDocument();

  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Virement');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [editingLines, setEditingLines] = useState(false);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>;
  }
  if (error || !doc) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-sm text-red-600">{error instanceof Error ? error.message : 'Document introuvable.'}</p>
        <Button variant="ghost" onClick={() => navigate('/facturation')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour à la liste
        </Button>
      </div>
    );
  }

  const isEstimate = doc.doc_type === 'estimate';
  const polarNumber = polarDocumentNumber(doc);
  const polarLocked = !!polarNumber;
  const canIssue = doc.status === 'draft' && !polarLocked && canAccessFeature(role, 'billing_issue');
  const canMarkPaid = !isEstimate && (doc.status === 'sent' || doc.status === 'overdue') && canAccessFeature(role, 'billing_mark_paid');
  const canVoid = (doc.status === 'sent' || doc.status === 'overdue' || doc.status === 'draft') && !polarLocked && canAccessFeature(role, 'billing_void');
  const canDelete = doc.status === 'draft' && !doc.polar_quote_id && canAccessFeature(role, 'billing_create');
  const canEditLines = doc.status === 'draft' && !polarLocked && canAccessFeature(role, 'billing_create');
  const hasPdf = polarPdfId(doc) != null;
  const canConvert =
    isEstimate && polarLocked && !doc.polar_invoice_id && canAccessFeature(role, 'billing_create');
  const canSend =
    polarSendId(doc) != null &&
    polarLocked &&
    !(isEstimate && doc.polar_invoice_id) &&
    doc.status !== 'sent' &&
    doc.status !== 'paid' &&
    doc.status !== 'void' &&
    canAccessFeature(role, 'invoices_send');
  const statusLabel = polarStatusLabel(doc);
  const statusTone: BillingDocumentStatus =
    statusLabel === 'Figé' ? 'sent' : doc.status;

  async function handleIssue() {
    try {
      await figerMut.mutateAsync({
        id: doc!.id,
        title: doc!.notes?.trim() || (dossier ? `${dossier.number} — ${dossier.title}` : 'Devis'),
      });
      setConfirmKind(null);
      toast.success('Figé dans Polar');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  async function handleConvert() {
    if (!serviceDate) {
      toast.error('Date de prestation obligatoire');
      return;
    }
    try {
      const invoice = await convertMut.mutateAsync({ id: doc!.id, serviceDate });
      setConfirmKind(null);
      toast.success('Facture Polar créée');
      navigate(`/facturation/${invoice.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  async function handleOpenPdf() {
    try {
      await pdfMut.mutateAsync(doc!);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'PDF Polar indisponible');
    }
  }

  async function handleSend() {
    try {
      await sendMut.mutateAsync(doc!.id);
      setConfirmKind(null);
      toast.success('Envoyé via Polar');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Envoi Polar impossible');
    }
  }

  async function handleMarkPaid() {
    try {
      await markPaidMut.mutateAsync({
        id: doc!.id,
        paid_at: new Date(paidAt).toISOString(),
        payment_method: paymentMethod,
      });
      toast.success('Marqué comme payé');
      setPaidModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  async function handleVoid() {
    try {
      await voidMut.mutateAsync(doc!.id);
      setConfirmKind(null);
      toast.success('Document annulé');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync(doc!.id);
      setConfirmKind(null);
      toast.success('Brouillon supprimé');
      navigate('/facturation');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  const confirmBusy =
    (confirmKind === 'figer' && figerMut.isPending) ||
    (confirmKind === 'convert' && convertMut.isPending) ||
    (confirmKind === 'send' && sendMut.isPending) ||
    (confirmKind === 'void' && voidMut.isPending) ||
    (confirmKind === 'delete' && deleteMut.isPending);

  function runConfirm() {
    if (confirmKind === 'figer') return handleIssue();
    if (confirmKind === 'convert') return handleConvert();
    if (confirmKind === 'send') return handleSend();
    if (confirmKind === 'void') return handleVoid();
    if (confirmKind === 'delete') return handleDelete();
  }

  return (
    <div className="main-container-gdm space-y-5">
      <Link
        to="/facturation"
        className="inline-flex items-center gap-1.5 text-sm text-cime-text-muted hover:text-cime-primary"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux documents
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-cime-text-muted">
            {BILLING_DOC_TYPE_LABELS[doc.doc_type]}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1
              className="text-[32px] font-bold leading-tight text-cime-primary"
              style={{ fontFamily: 'var(--cime-font-heading)' }}
            >
              {polarNumber ?? <span className="text-neutral-400 italic">Brouillon</span>}
            </h1>
            <span
              className={cn(
                'inline-flex h-[22px] items-center rounded-full px-2.5 text-xs font-semibold',
                STATUS_CLASSES[statusTone],
              )}
            >
              {statusLabel}
            </span>
          </div>
          {dossier && (
            <p className="text-sm text-neutral-500 mt-1">
              Dossier{' '}
              <Link to={`/dossiers/${dossier.id}`} className="text-cime-primary hover:underline">
                <span>{dossier.number}</span> — {dossier.title}
              </Link>
            </p>
          )}
          {doc.source_estimate_id && (
            <p className="text-xs text-neutral-500 mt-0.5">
              Issue du devis{' '}
              <Link to={`/facturation/${doc.source_estimate_id}`} className="text-cime-primary hover:underline">
                voir le devis source
              </Link>
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {canIssue && (
            <Button variant="primary" onClick={() => setConfirmKind('figer')} isLoading={figerMut.isPending}>
              Figer dans Polar
            </Button>
          )}
          {hasPdf && (
            <Button variant="secondary" onClick={handleOpenPdf} isLoading={pdfMut.isPending}>
              <FileText className="w-4 h-4 mr-1.5" /> Ouvrir le PDF
            </Button>
          )}
          {canConvert && (
            <Button variant="primary" onClick={() => setConfirmKind('convert')} isLoading={convertMut.isPending}>
              <FilePlus className="w-4 h-4 mr-1.5" /> Convertir en facture
            </Button>
          )}
          {canSend && (
            <Button variant="primary" onClick={() => setConfirmKind('send')} isLoading={sendMut.isPending}>
              <Send className="w-4 h-4 mr-1.5" /> Envoyer
            </Button>
          )}
          {canMarkPaid && (
            <Button variant="primary" onClick={() => setPaidModalOpen(true)}>
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marquer payée
            </Button>
          )}
          {canVoid && (
            <Button variant="secondary" onClick={() => setConfirmKind('void')} isLoading={voidMut.isPending}>
              <Ban className="w-4 h-4 mr-1.5" /> Annuler
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setConfirmKind('delete')} isLoading={deleteMut.isPending}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>
      </div>

    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,440px)]">
    <div className="min-w-0 space-y-5">
      {/* Métadonnées */}
      <Card>
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-5">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Émission</p>
            <p className="text-neutral-900">{doc.issue_date ? format(parseISO(doc.issue_date), 'dd MMMM yyyy', { locale: fr }) : '—'}</p>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Échéance</p>
            <p className="text-neutral-900">{doc.due_date ? format(parseISO(doc.due_date), 'dd MMMM yyyy', { locale: fr }) : '—'}</p>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Taux TVA</p>
            <p className="text-neutral-900">{Number(doc.vat_rate)} %</p>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">PDF Polar</p>
            <p className={hasPdf ? 'text-[#2f7a55]' : 'text-neutral-900'}>{hasPdf ? 'Disponible' : 'Après figer'}</p>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Paiement</p>
            <p className="text-neutral-900">
              {doc.paid_at
                ? `${formatAppDate(doc.paid_at)} (${doc.payment_method ?? 'N/C'})`
                : '—'}
            </p>
          </div>
        </div>
      </Card>

      {/* Lignes */}
      {editingLines ? (
        <BillingLinesEditor
          documentId={doc.id}
          vatRate={Number(doc.vat_rate)}
          onClose={() => setEditingLines(false)}
        />
      ) : (
        <Card
          flush
          headerClassName="px-4 py-2.5"
          header={
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Lignes</span>
              {canEditLines && (
                <Button variant="ghost" size="sm" onClick={() => setEditingLines(true)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Éditer
                </Button>
              )}
            </div>
          }
        >
          {!lines || lines.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-500">Aucune ligne</p>
          ) : (
            <table className="cime-table text-sm">
              <thead>
                <tr>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-left">Description</th>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-left">Nature</th>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-right">Quantité</th>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-right">PU HT</th>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-right">TVA</th>
                  <th className="py-2 px-2.5 first:pl-4 last:pr-4 text-right">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-neutral-700 whitespace-pre-wrap">{l.description}</td>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-neutral-500">{LINE_NATURE_LABELS[l.nature as LineNature] ?? l.nature}</td>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-right tabular-nums text-neutral-700">
                      {Number(l.quantity).toFixed(l.unit === 'hour' ? 2 : 0)} {l.unit === 'hour' ? 'h' : ''}
                    </td>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-right cime-amount">{formatEuros(l.unit_price)}</td>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-right whitespace-nowrap text-neutral-500">{formatVatRate(l.vat_rate ?? doc.vat_rate)}</td>
                    <td className="py-2.5 px-2.5 first:pl-4 last:pr-4 text-right cime-amount">{formatEuros(l.ht_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Totaux */}
      <Card>
        <div className="ml-auto w-full max-w-sm space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-neutral-600">Total HT</span><span className="cime-amount">{formatEuros(doc.ht_total)}</span></div>
          {/* TVA ventilée par taux quand la pièce en mélange plusieurs ; le total reste celui de la base. */}
          {(() => {
            const byRate = computeLineTotals(lines ?? [], Number(doc.vat_rate)).byRate;
            return byRate.length > 1 ? (
              byRate.map((r) => (
                <div key={r.rate} className="flex justify-between">
                  <span className="text-neutral-600">TVA {formatVatRate(r.rate)} <span className="text-neutral-400">sur {formatEuros(r.ht)}</span></span>
                  <span className="cime-amount">{formatEuros(r.vat)}</span>
                </div>
              ))
            ) : (
              <div className="flex justify-between"><span className="text-neutral-600">TVA ({formatVatRate(byRate[0]?.rate ?? doc.vat_rate)})</span><span className="cime-amount">{formatEuros(doc.vat_total)}</span></div>
            );
          })()}
          <div className="flex justify-between border-t border-neutral-300 pt-1.5 mt-1.5 font-semibold text-base">
            <span>Total TTC</span>
            <span className="cime-amount">{formatEuros(doc.ttc_total)}</span>
          </div>
        </div>
      </Card>

      {canEditLines ? (
        <BillingNotesEditor documentId={doc.id} initialNotes={doc.notes ?? ''} />
      ) : doc.notes ? (
        <Card headerClassName="px-4 py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Notes</span>}>
          <p className="text-sm text-neutral-700 whitespace-pre-wrap">{doc.notes}</p>
        </Card>
      ) : null}

      <Dialog
        open={confirmKind !== null}
        onOpenChange={(open) => {
          if (!open && !confirmBusy) setConfirmKind(null);
        }}
      >
        <DialogContent className="sm:max-w-md bg-white rounded-cime border-neutral-200">
          {confirmKind && (
            <>
              <DialogHeader>
                <DialogTitle className="text-cime-text-primary">
                  {CONFIRM_COPY[confirmKind].title}
                </DialogTitle>
                <DialogDescription className="text-cime-text-muted">
                  {CONFIRM_COPY[confirmKind].body}
                </DialogDescription>
              </DialogHeader>
              {confirmKind === 'convert' && (
                <Input
                  id="polar-service-date"
                  type="date"
                  label="Date de prestation"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value)}
                />
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={confirmBusy}
                  onClick={() => setConfirmKind(null)}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant={CONFIRM_COPY[confirmKind].variant}
                  isLoading={confirmBusy}
                  disabled={confirmKind === 'convert' && !serviceDate}
                  onClick={() => void runConfirm()}
                >
                  {CONFIRM_COPY[confirmKind].action}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modale "Marquer payée" simplifiée */}
      {paidModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Marquer comme payée</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-700">Date du paiement</label>
                <input
                  type="date"
                  value={paidAt}
                  onChange={(e) => setPaidAt(e.target.value)}
                  className="mt-1 w-full h-10 px-3 text-sm rounded-cime border border-neutral-300"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700">Méthode</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mt-1 w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white"
                >
                  <option>Virement</option>
                  <option>Chèque</option>
                  <option>Carte bancaire</option>
                  <option>Espèces</option>
                  <option>Autre</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <Button variant="ghost" onClick={() => setPaidModalOpen(false)}>Annuler</Button>
              <Button variant="primary" onClick={handleMarkPaid} isLoading={markPaidMut.isPending}>Confirmer</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
    <BillingSheet
      doc={doc}
      lines={lines ?? []}
      dossierLabel={dossier ? `${dossier.number} — ${dossier.title}` : null}
    />
    </div>
    </div>
  );
}
